import axios, { AxiosError, AxiosResponse } from "axios";
import { API_URL } from "./env";

const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// 🔹 Response Interceptor
apiClient.interceptors.response.use(
  (response) => response, // pass through success
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Only handle if 401 & specific error message
    if (
      error.response?.status === 401 &&
      // (error.response.data as any)?.message === "No token provided" &&
      !originalRequest?._retry // prevent infinite loop
    ) {
      originalRequest._retry = true;

      try {
        // Attempt token refresh
        const refreshResponse: AxiosResponse<any> = await axios.post(
          `${API_URL}/user/refresh-token`,
          {},
          { withCredentials: true }
        );

        if (refreshResponse.status === 200) {
          // ✅ Retry original request with new token
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // ❌ Refresh also failed → reject
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

//
// 🔹 Helper functions
//
const postData = async <T = any>(
  url: string,
  data: object
): Promise<T | undefined> => {
  try {
    const res: AxiosResponse<T> = await apiClient.post(url, data);
    return res.data;
  } catch (err: unknown) {
    const error = err as AxiosError;
    throw {
      status: error.response?.status,
      ...(error.response?.data ? (error.response.data as object) : {}),
    };
  }
};

const postDataFormData = async <T = any>(
  url: string,
  data: FormData
): Promise<T | undefined> => {
  try {
    const res: AxiosResponse<T> = await apiClient.post(url, data, {
      headers: { "Content-Type": "multipart/form-data" }, // ✅ fix header
    });
    return res.data;
  } catch (err: unknown) {
    const error = err as AxiosError;
    throw {
      status: error.response?.status,
      ...(error.response?.data ? (error.response.data as object) : {}),
    };
  }
};

const otherRequest = async <T = any>(
  method: string,
  url: string,
  data: object
): Promise<T> => {
  try {
    const res: AxiosResponse<T> = await apiClient({
      method,
      url,
      data,
    });
    return res.data;
  } catch (err: unknown) {
    const error = err as AxiosError;
    throw {
      status: error.response?.status,
      ...(error.response?.data ? (error.response.data as object) : {}),
    };
  }
};

const getData = async (url: string, params: object = {}) => {
  let newUrl = url;

  if (Object.entries(params).length > 0) {
    const stringParams: Record<string, string> = Object.fromEntries(
      Object.entries(params).map(([key, value]) => [key, String(value)])
    );

    const queryParams = new URLSearchParams(stringParams).toString();
    newUrl = queryParams ? `${newUrl}?${queryParams}` : newUrl;
  }

  return apiClient(newUrl)
    .then((res) => res.data)
    .catch((err) => {
      return { status: err.response?.status, ...err.response?.data };
    });
};

export const API = {
  socialLogin: (data: object) => postData(`/user/social-login`, data),
  login: (data: object) => postData(`/user/login`, data),
  signup: (data: object) => postData(`/user/signup`, data),
  verifyUser: (data: object) => postData(`/user/verify-email`, data),
  getUserProfile: () => getData(`/user/profile`),

  forgotRequest: (data: object) => postData(`/user/forgot-request`, data),
  verifyForgotRequest: (data: object) =>
    postData(`/user/verify-forgot-request`, data),
  updatePassword: (data: object) => postData(`/user/update-password`, data),
  resendForgotOtp: (data: object) => postData(`/user/update-password`, data),

  refreshAuthToken: () => postData(`/user/refresh-token`, {}),
  updateProfile: (data: any) => postDataFormData(`/user/update-profile`, data),
  changePassword: (data: object) => postData(`/user/change-password`, data),
  logout: () => postData(`/user/logout`, {}),

  // Agent
  editVenueSession: (data: object) =>
    postData(`/agent/edit-venue-session`, data),
  searchSpaceSession: (data: object) =>
    postData(`/agent/search-space-session`, data),
  tokenStatus: () => getData(`/agent/token-status`),
  trackTokens: () => getData(`/agent/track-tokens`),

  // Venues
  createNewVenue: (data: object) => postData(`/venue/create`, data),
  updateVanue: (data: object) => postData(`/venue/update`, data),
  deleteVenue: (data: object) => postData(`/venue/delete`, data),
  getUserVanues: (data: object) => getData(`/venue/user-venues`, data),
  getVanue: (id: string) => getData(`/venue/single/${id}`),

  getSpaceTypes: (data: object) => getData(`/tag/query`, data),

  createFeaturesTag: (data: object) => postData(`/feature/create`, data),
  deleteFeaturesTag: (data: object) => postData(`/feature/delete`, data),
  createTag: (data: object) => postData(`/tag/create`, data),
  deleteTag: (data: object) => postData(`/tag/delete`, data),

  getContentDetails: (data: object) => getData(`/detail/query`, data),
  createContentDetails: (data: object) => postData(`/detail/create`, data),
  updateContentDetails: (data: object) => postData(`/detail/update`, data),
  deleteContentDetails: (data: object) => postData(`/detail/delete`, data),

  // Spaces
  createNewSpace: (data: object) => postData(`/space/create`, data),
  updateSpace: (data: object) => postData(`/space/update`, data),
  updateSpaceImages: (data: FormData) =>
    postDataFormData(`/space/update`, data),
  deleteSpace: (data: object) => postData(`/space/delete`, data),
  getDateSlots: (data: object) => getData(`/availability/date-slots`, data),
  createSpaceAvailability: (data: object) =>
    postData(`/availability/create`, data),
  updateSpaceAvailability: (data: object) =>
    postData(`/availability/update`, data),
  deleteSpaceAvailability: (data: object) =>
    postData(`/availability/delete`, data),

  // getAdminVenueSpaceList: (data: object) => getData(`/space/user-spaces`, data),

  getVenueSpaceList: (data: object) => getData(`/space/user-spaces`, data),
  getSpace: (id: string) => getData(`/space/single/${id}`),
  getMySpace: (data: object) => postData(`/space/user-spaces`, data),
  getSearchSpace: (data: object) => getData(`/space/query`, data),

  toggleUserFavorite: (data: object) =>
    postData(`/space/toggle/favorite`, data),
  getUserFavoritesList: (data: object) => getData(`/space/user/favorite`, data),
  getUserFavorites: () => getData(`/space/user/favorite/ids`),

  // Booking
  createBooking: (data: object) => postData(`/booking/book`, data),
  confirmPayment: (data: object) => postData(`/payment/confirm`, data),
  failedBookingPayment: (data: object) => postData(`/payment/failed`, data),
  getUnavailableDate: (spotId: string, data: object) =>
    getData(`${API_URL}/space/booked-dates/${spotId}`, data),
  getBookingDetails: (id: string) => getData(`/booking/single/${id}`),
  cancelSpaceBooking: (data: object) => postData(`/booking/cancel`, data),
  updateBooking: (data: object) => postData(`/booking/update`, data),
  timeChangeRequest: (data: object) => postData(`/booking/timechange`, data),
  acceptRequest: (data: object) => postData(`/booking/accept-request`, data),
  cancelRequest: (data: object) => postData(`/booking/deny-request`, data),

  // Get User Bookings
  getPaidBookings: (data: object) => getData(`/booking/client-bookings`, data),
  getCollectedBookings: (data: object) =>
    getData(`/booking/host-bookings`, data),
  getBookingRequests: (data: object) =>
    getData(`/booking/combined-booking-timechange-payment-pending`, data),
  getBookingHistory: (data: object) =>
    getData(`/booking/client-past-bookings`, data),
  getUserPayments: (data: object) => getData(`/payment/host`, data),
  userCheckIn: (data: object) => postData(`/booking-log/user-checkin`, data),
  userCheckOut: (data: object) => postData(`/booking-log/user-checkout`, data),
  hostCheckIn: (data: object) => postData(`/booking-log/host-checkin`, data),
  hostCheckOut: (data: object) => postData(`/booking-log/host-checkout`, data),

  // User Messges / Conversations
  getUserConversations: (data: object) => getData(`/chat/chats`, data),
  getUserChat: (data: { userId: string }) =>
    getData(`/chat/single/${data.userId}`, data),
  sendUserMessage: (data: object) => postData(`/chat/send`, data),
  updateUserMessage: (data: object) => postData(`/chat/edit`, data),
  deleteUserMessage: (data: object) => postData(`/chat/delete`, data),

  getNotifications: (data: object) => getData(`/chat/notifications`, data),
  readNotification: (data: object) => postData(`/chat/notification/read`, data),

  // Admin List
  getAdminVanues: (data: object) => getData(`/venue/query`, data),
  getAdminBookingList: (data: object) => getData(`/booking/query`, data),
  getAdminPayments: (data: object) => getData(`/payment/query`, data),
  adminBlockVenue: (data: object) => postData(`/venue/block-unblock`, data),
  getUsersList: (data: object) => getData(`/user/query`, data),
  blockUserItem: (data: object) => postData(`/user/block-unblock`, data),

  getStripeAccountStatus: () => getData(`/connect/account-status`),
  connectStripeAccount: (data: object) =>
    postData(`/connect/create-account`, data),
  getBalance: () => getData(`/payout/balance`),
  getPayoutHistory: (data: object) => getData(`/payout/history`, data),
  executeWithdraw: (data: object) => postData(`/payout/withdraw`, data),
  retryWithdraw: (data: object) => postData(`/payout/retry`, data),
  getPayoutStatus: (id: string) =>
    getData(`/payout/single-payout-status/${id}`),
  getPayoutDetails: (id: string) => getData(`/payout/single-payout/${id}`),
};
